import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { setAuthToken } from "@/lib/api";
import { toTitleCase } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Search, CheckCircle, AlertCircle, Upload, Loader2, Trash2, Plus, MapPin } from "lucide-react";

import STATE_CITIES, { INDIAN_STATES } from "@/data/indianStatesCities";

const TRANSASIA_LOGO = "https://customer-assets.emergentagent.com/job_ac147b83-bb9e-4da1-b506-44afecc6b2ce/artifacts/4lmb9h3d_IMG-20260413-WA0117.jpg";

// Point 14: helper image shown on the Instrument Verification screen (below the
// Validate & Continue button) — a sample TransAsia invoice highlighting the REG.ID.
const INSTRUMENT_HELP_IMAGE = "/api/files/branding/reg_id_help.jpg";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const Row = ({ label, value, highlight, status }) => {
  if (!value) return null;
  let valClass = "font-semibold text-gray-800";
  if (highlight) valClass = "font-bold text-[#008A99] text-sm";
  if (status) {
    const s = String(status).toUpperCase();
    if (s === "ACTIVE") valClass = "font-bold text-green-600";
    else if (s === "EXPIRED" || s === "TERMINATED") valClass = "font-bold text-red-600";
    else valClass = "font-bold text-amber-600";
  }
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-gray-500 text-[11px] uppercase tracking-wider">{label}</span>
      <span className={`text-right ${valClass}`}>{value}</span>
    </div>
  );
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1); // 1=instrument, 2=details, 3=lab-profile, 4=otp, 5=kyc, 6=review
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1: Instrument
  const [serialNumber, setSerialNumber] = useState("");
  const [lookupMode, setLookupMode] = useState("registry"); // registry | serial (Point 34)
  const [serialInput, setSerialInput] = useState("");
  const [modelName, setModelName] = useState("");
  const [serialModels, setSerialModels] = useState([]); // Point 39: models mapped to the serial (from ERP)
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsFetched, setModelsFetched] = useState(false);
  const [instrumentData, setInstrumentData] = useState(null);
  const [instruments, setInstruments] = useState([]);
  const [instrumentStatus, setInstrumentStatus] = useState(null); // found, not_found, blocked, error
  const [lookupMessage, setLookupMessage] = useState("");
  const [resolvedRegistryId, setResolvedRegistryId] = useState("");

  // Step 2: User Details
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Step 3: OTP
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  // Step 3 (Point 22): Laboratory profile
  const [labName, setLabName] = useState("");
  const [labTel, setLabTel] = useState("");
  const [labAddress, setLabAddress] = useState("");
  const [usesErba, setUsesErba] = useState(null); // null | true | false
  const [labRows, setLabRows] = useState([]); // {is_erba, instrument, instrument_name, reagent, reagent_name, avg_tests}
  const [allInstruments, setAllInstruments] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  useEffect(() => {
    API.get("/instruments").then(r => setAllInstruments(r.data || [])).catch(() => {});
    API.get("/products?limit=1000").then(r => setAllProducts(r.data?.products || [])).catch(() => {});
  }, []);
  const reagentsFor = (code) => allProducts.filter(p => {
    const codes = (p.instrument_codes && p.instrument_codes.length ? p.instrument_codes : [p.instrument_code]).filter(Boolean);
    return codes.includes(code);
  }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const addRow = (isErba) => setLabRows(prev => [...prev, { is_erba: isErba, instrument: "", instrument_name: "", reagent: "", reagent_name: "", avg_tests: "" }]);
  const updateRow = (idx, patch) => setLabRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRow = (idx) => setLabRows(prev => prev.filter((_, i) => i !== idx));
  const chooseErba = (val) => { setUsesErba(val); setLabRows(prev => prev.length === 0 ? [{ is_erba: val, instrument: "", instrument_name: "", reagent: "", reagent_name: "", avg_tests: "" }] : prev); };

  // Referral code (optional, captured on Step 2 details screen)
  const [referralCode, setReferralCode] = useState("");
  const [referralStatus, setReferralStatus] = useState({ checking: false, valid: null, name: "" });
  const validateReferral = async (code) => {
    const norm = (code || "").trim().toUpperCase();
    if (!norm) { setReferralStatus({ checking: false, valid: null, name: "" }); return; }
    setReferralStatus({ checking: true, valid: null, name: "" });
    try {
      const { data } = await API.get(`/referral/validate?code=${encodeURIComponent(norm)}`);
      setReferralStatus({ checking: false, valid: true, name: data.referrer_first_name || "" });
    } catch {
      setReferralStatus({ checking: false, valid: false, name: "" });
    }
  };

  // Step 4: KYC
  const [panNumber, setPanNumber] = useState("");
  const [panFile, setPanFile] = useState(null);
  const [gstFile, setGstFile] = useState(null);
  const [labFile, setLabFile] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Point 26 & 27 — mandatory geolocation capture at the final (KYC) step.
  const [geoLat, setGeoLat] = useState(null);
  const [geoLng, setGeoLng] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | requesting | granted | denied | unavailable
  const [geoMsg, setGeoMsg] = useState("");
  const requestLocation = () => {
    if (!("geolocation" in navigator)) { setGeoStatus("unavailable"); setGeoMsg("Your device does not support location. Please enable GPS to continue."); return; }
    setGeoStatus("requesting"); setGeoMsg("");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeoLat(pos.coords.latitude); setGeoLng(pos.coords.longitude); setGeoStatus("granted"); setGeoMsg(""); },
      (err) => {
        if (err && err.code === 1) { setGeoStatus("denied"); setGeoMsg("Location permission is OFF. Please turn ON location access for this app in your settings, then tap Retry."); }
        else if (err && err.code === 2) { setGeoStatus("unavailable"); setGeoMsg("We couldn't detect your location. Please switch ON your device location / GPS and tap Retry."); }
        else { setGeoStatus("unavailable"); setGeoMsg("Location request timed out. Please tap Retry."); }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };
  useEffect(() => { if (step === 5 && geoStatus === "idle") requestLocation(); /* eslint-disable-next-line */ }, [step]);
  const [uploading, setUploading] = useState({});
  const [fileHashes, setFileHashes] = useState({});

  const filteredStates = INDIAN_STATES.filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()));
  const availableCities = state ? (STATE_CITIES[state] || []) : [];
  const filteredCities = availableCities.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));

  // Point 39: fetch the model(s) actually mapped to the typed serial number
  // from the ERP, so the model dropdown shows the correct PRODUCT_SHORT_NAME.
  const fetchModelsForSerial = async () => {
    const s = serialInput.trim();
    if (!s) { setError("Enter a Serial Number first"); return; }
    setModelsLoading(true); setError(""); setModelsFetched(false); setSerialModels([]); setModelName("");
    try {
      const { data } = await API.post("/auth/serial-models", { serial_number: s });
      const models = data.models || [];
      setSerialModels(models);
      setModelsFetched(true);
      if (models.length === 1) setModelName(models[0]);
    } catch (e) {
      setModelsFetched(true);
      setSerialModels([]);
    } finally { setModelsLoading(false); }
  };

  const validateInstrument = async () => {
    if (lookupMode === "serial") {
      if (!serialInput.trim()) { setError("Enter a Serial Number"); return; }
      if (!modelName.trim()) { setError("Select the instrument model"); return; }
      setLoading(true); setError(""); setLookupMessage("");
      try {
        const { data } = await API.post("/auth/validate-instrument", {
          serial_number: serialInput.trim(),
          product_short_name: modelName.trim(),
        });
        setInstrumentStatus(data.status);
        setLookupMessage(data.message || "");
        setResolvedRegistryId(data.registry_id || "");
        if (data.status === "found" && data.instruments?.length > 0) {
          setInstruments(data.instruments);
          setInstrumentData(data.instruments[0]);
          setSerialNumber(data.registry_id || serialInput.trim());
          setStep(2);
        } else if (data.status === "blocked") {
          setInstrumentData(null); setInstruments([]);
        } else if (data.status === "need_model") {
          setError(data.message || "Please select the instrument model.");
        } else {
          // not_found / error — let them continue but keep the typed serial
          setInstrumentData(null); setInstruments([]);
          setSerialNumber(serialInput.trim());
          setStep(2);
        }
      } catch (e) {
        setError(e.response?.data?.detail || "Validation failed");
      } finally { setLoading(false); }
      return;
    }
    if (!serialNumber.trim()) { setError("Enter a Registry ID"); return; }
    setLoading(true); setError(""); setLookupMessage("");
    try {
      const { data } = await API.post("/auth/validate-instrument", { registry_id: serialNumber.trim() });
      setInstrumentStatus(data.status);
      setLookupMessage(data.message || "");
      setResolvedRegistryId(data.registry_id || "");
      if (data.status === "found" && data.instruments?.length > 0) {
        setInstruments(data.instruments);
        setInstrumentData(data.instruments[0]);
        setStep(2);
      } else if (data.status === "blocked") {
        // Stay on step 1 — show the ERP message clearly
        setInstrumentData(null);
        setInstruments([]);
      } else {
        setInstrumentData(null);
        setInstruments([]);
        setStep(2);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Validation failed");
    } finally { setLoading(false); }
  };

  const continueToLabProfile = () => {
    if (!fullName.trim()) { setError("Full name is required"); return; }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email.trim())) { setError("Enter a valid email address"); return; }
    if (!mobile || mobile.length < 10) { setError("Enter valid 10-digit mobile"); return; }
    if (!state) { setError("Select your state"); return; }
    if (!city) { setError("Select your city"); return; }
    setError("");
    setStep(3);
  };

  const sendOtp = async () => {
    // Point 22 — lab profile is compulsory.
    if (!labName.trim()) { setError("Lab name is required"); return; }
    if (!labTel.trim()) { setError("Lab telephone number is required"); return; }
    if (!labAddress.trim()) { setError("Lab address is required"); return; }
    if (usesErba === null) { setError("Please tell us if you use an Erba model"); return; }
    if (labRows.length === 0) { setError("Add at least one instrument & reagent"); return; }
    for (let i = 0; i < labRows.length; i++) {
      const r = labRows[i];
      if (!String(r.instrument || "").trim()) { setError(`Row ${i + 1}: select or enter an instrument`); return; }
      if (!String(r.reagent || "").trim()) { setError(`Row ${i + 1}: select or enter a reagent`); return; }
      if (!String(r.avg_tests || "").trim()) { setError(`Row ${i + 1}: enter average tests per month`); return; }
    }
    setLoading(true); setError("");
    try {
      await API.post("/auth/register/send-otp", { mobile });
      setOtpSent(true);
      setStep(4);
    } catch (e) { setError(e.response?.data?.detail || "Failed to send OTP"); }
    finally { setLoading(false); }
  };

  const verifyAndRegister = async () => {
    if (!otp || otp.length !== 6) { setError("Enter 6-digit OTP"); return; }
    setLoading(true); setError("");
    try {
      const { data } = await API.post("/auth/register", {
        full_name: fullName, email, mobile, otp, city, state,
        serial_number: serialNumber,
        registry_id: resolvedRegistryId || serialNumber,
        instrument_data: instrumentData,
        instruments,
        referred_by_code: referralStatus.valid ? referralCode.trim().toUpperCase() : "",
        lab_name: labName.trim(),
        lab_tel: labTel.trim(),
        lab_address: labAddress.trim(),
        uses_erba: usesErba,
        lab_profile: labRows.map(r => ({
          is_erba: r.is_erba,
          instrument: r.instrument, instrument_name: r.instrument_name || r.instrument,
          reagent: r.reagent, reagent_name: r.reagent_name || r.reagent,
          avg_tests: r.avg_tests,
        })),
      });
      setAuthToken(data.access_token);
      login(data.user, data.access_token);
      setStep(5);
    } catch (e) { setError(e.response?.data?.detail || "Registration failed"); }
    finally { setLoading(false); }
  };

  const ALLOWED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
  const ALLOWED_FILE_EXTS = [".pdf", ".jpg", ".jpeg", ".png"];
  const FIELD_LABELS = { pan_photo: "PAN photograph", gst_cert: "GST certificate", lab_cert: "Lab certificate" };

  const hashFile = async (file) => {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
  };

  const uploadFile = async (file, field, setFile) => {
    const nameLc = (file.name || "").toLowerCase();
    const okType = ALLOWED_FILE_TYPES.includes(file.type) || ALLOWED_FILE_EXTS.some(e => nameLc.endsWith(e));
    if (!okType) {
      setError("Only PDF, JPG, or PNG files are allowed.");
      if (setFile) setFile(null);
      return;
    }
    // Client-side duplicate check so we can surface the error instantly
    try {
      const newHash = await hashFile(file);
      const clash = Object.entries(fileHashes).find(([k, v]) => k !== field && v === newHash);
      if (clash) {
        setError(`This image is already uploaded as your ${FIELD_LABELS[clash[0]] || clash[0]}. Please upload a different document for ${FIELD_LABELS[field] || field}.`);
        if (setFile) setFile(null);
        return;
      }
      setFileHashes(prev => ({ ...prev, [field]: newHash }));
    } catch { /* fall through — backend will still catch duplicates */ }

    const fd = new FormData();
    fd.append("file", file);
    setUploading(prev => ({ ...prev, [field]: true }));
    setError("");
    try {
      await API.post(`/kyc/upload?field=${field}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) {
      setError(e.response?.data?.detail || `Upload failed: ${field}`);
      // Roll back local hash & file on server-side rejection (e.g. duplicate caught by backend)
      setFileHashes(prev => { const n = { ...prev }; delete n[field]; return n; });
      if (setFile) setFile(null);
    }
    finally { setUploading(prev => ({ ...prev, [field]: false })); }
  };

  const submitKyc = async () => {
    if (!labFile) { setError("Lab certificate / registration is required"); return; }
    if (!termsAccepted) { setError("Accept Terms & Conditions"); return; }
    if (geoStatus !== "granted" || !geoLat || !geoLng) { setError("Please enable location sharing to submit your registration."); return; }
    setLoading(true); setError("");
    try {
      await API.post("/kyc/submit", { pan_number: panNumber, latitude: geoLat, longitude: geoLng });
      // Clear local auth state — user cannot access the app until admin approves.
      setAuthToken(null);
      try { localStorage.removeItem("user"); } catch {}
      setStep(6);
    } catch (e) { setError(e.response?.data?.detail || "KYC submission failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F7FA] flex flex-col" data-testid="register-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate("/login")} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="register-back-btn">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <img src={TRANSASIA_LOGO} alt="TransAsia" className="h-8" />
        <span className="text-sm font-semibold text-gray-700">New Registration</span>
      </div>

      {/* Progress */}
      <div className="bg-white px-4 py-2 border-b">
        <div className="flex gap-1 max-w-md mx-auto">
          {[1,2,3,4,5].map(s => (
            <div key={s} className={`flex-1 h-1.5 rounded-full transition-all ${s <= step ? 'bg-[#008A99]' : 'bg-gray-200'}`} />
          ))}
        </div>
        <p className="text-[10px] text-center text-gray-400 mt-1">Step {Math.min(step,5)} of 5</p>
      </div>

      <div className="flex-1 max-w-md mx-auto w-full px-4 py-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2" data-testid="register-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        {/* Step 1: Instrument Validation */}
        {step === 1 && (
          <div className="space-y-5" data-testid="step-instrument">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Instrument Verification</h2>
              <p className="text-sm text-gray-500 mt-1">{lookupMode === "serial" ? "Enter your instrument Serial Number and model" : "Enter your Registry ID (customer account number)"}</p>
            </div>

            {/* Point 34: lookup mode toggle — Registry ID or Serial Number */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-xl" data-testid="lookup-mode-toggle">
              <button type="button" onClick={() => { setLookupMode("registry"); setError(""); setInstrumentStatus(null); setLookupMessage(""); }}
                className={`py-2 rounded-lg text-sm font-semibold transition-colors ${lookupMode === "registry" ? "bg-white text-[#008A99] shadow-sm" : "text-gray-500"}`} data-testid="mode-registry-btn">
                Registry ID
              </button>
              <button type="button" onClick={() => { setLookupMode("serial"); setError(""); setInstrumentStatus(null); setLookupMessage(""); }}
                className={`py-2 rounded-lg text-sm font-semibold transition-colors ${lookupMode === "serial" ? "bg-white text-[#008A99] shadow-sm" : "text-gray-500"}`} data-testid="mode-serial-btn">
                Serial Number
              </button>
            </div>

            {lookupMode === "registry" ? (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Registry ID</label>
                <div className="mt-1.5 flex gap-2">
                  <input type="text" value={serialNumber} onChange={e => { setSerialNumber(e.target.value); setInstrumentStatus(null); setLookupMessage(""); }}
                    placeholder="e.g. 9621" className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]" data-testid="serial-number-input" />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Serial Number</label>
                  <div className="mt-1.5 flex gap-2">
                    <input type="text" value={serialInput} onChange={e => { setSerialInput(e.target.value); setInstrumentStatus(null); setLookupMessage(""); setModelsFetched(false); setSerialModels([]); setModelName(""); }}
                      placeholder="e.g. K10012119185" className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]" data-testid="serial-input" />
                    <button type="button" onClick={fetchModelsForSerial} disabled={modelsLoading || !serialInput.trim()}
                      className="px-4 py-3 bg-[#0A2540] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 whitespace-nowrap" data-testid="fetch-models-btn">
                      {modelsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find model"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Instrument Model</label>
                  {modelsFetched && serialModels.length > 0 ? (
                    <select value={modelName} onChange={e => { setModelName(e.target.value); setInstrumentStatus(null); setLookupMessage(""); }}
                      className="mt-1.5 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]" data-testid="model-select">
                      <option value="">Select your model…</option>
                      {serialModels.map((m, idx) => (<option key={idx} value={m}>{toTitleCase(m)}</option>))}
                    </select>
                  ) : (
                    <>
                      <input type="text" list="model-options" value={modelName} onChange={e => { setModelName(e.target.value); setInstrumentStatus(null); setLookupMessage(""); }}
                        placeholder="Tap 'Find model', or type it exactly as on your invoice" className="mt-1.5 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]" data-testid="model-input" />
                      <datalist id="model-options">
                        {allInstruments.map((i, idx) => (<option key={idx} value={i.name || i.instrument_code} />))}
                      </datalist>
                    </>
                  )}
                  {modelsFetched && serialModels.length > 0 && (
                    <p className="text-[11px] text-green-600 mt-1 font-semibold" data-testid="models-found-hint">Model mapped to this serial number — select to continue.</p>
                  )}
                  {modelsFetched && serialModels.length === 0 && (
                    <p className="text-[11px] text-amber-600 mt-1" data-testid="models-none-hint">Couldn't auto-detect the model for this serial. Please select or type your model exactly as printed on your invoice.</p>
                  )}
                  {!modelsFetched && (
                    <p className="text-[11px] text-gray-400 mt-1">Tap "Find model" to load the model mapped to your serial number.</p>
                  )}
                </div>
              </div>
            )}

            {/* Blocked message (Govt/Regional/National) */}
            {instrumentStatus === "blocked" && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700" data-testid="blocked-message">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Not eligible for the loyalty program</p>
                    <p className="mt-1 text-red-600">{lookupMessage || "This customer account is not allowed to enroll."}</p>
                  </div>
                </div>
              </div>
            )}

            <button onClick={validateInstrument} disabled={loading}
              className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="validate-instrument-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4" /> Validate & Continue</>}
            </button>
            {INSTRUMENT_HELP_IMAGE && (
              <div data-testid="instrument-help-block">
                <p className="text-xs font-semibold text-gray-500 text-center mb-2">Your Registry ID (REG.ID) is printed on your TransAsia invoice</p>
                <a href={INSTRUMENT_HELP_IMAGE} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={INSTRUMENT_HELP_IMAGE} alt="Where to find your Registry ID on your TransAsia invoice"
                    className="w-full rounded-xl border border-gray-200 shadow-sm hover:opacity-90 transition-opacity" data-testid="instrument-help-image" />
                </a>
                <p className="text-[10px] text-gray-400 text-center mt-1">Tap the image to view it larger</p>
              </div>
            )}
            <button onClick={() => { setInstrumentStatus("skipped"); setStep(2); }}
              className="w-full py-3 border-2 border-[#008A99] text-[#008A99] rounded-xl font-semibold text-sm hover:bg-[#E0F4F5]/40 transition-colors" data-testid="skip-instrument-btn">
              Skip
            </button>
          </div>
        )}

        {/* Step 2: User Details */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-details">
            {instrumentData && instrumentStatus === "found" && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 mb-2 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-green-800">Instrument Found</p>
                      {resolvedRegistryId && <p className="text-[11px] text-green-600 font-semibold">Registry ID: {resolvedRegistryId}</p>}
                    </div>
                  </div>
                  {instruments.length > 1 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-600 text-white">{instruments.length} INSTRUMENTS</span>
                  )}
                </div>
                {instruments.map((inst, idx) => (
                  <div key={idx} className={`bg-white rounded-xl p-3 ${idx > 0 ? "mt-2" : ""} border border-green-100`}>
                    {instruments.length > 1 && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Instrument {idx + 1}</p>}
                    <div className="grid grid-cols-1 gap-y-1.5 text-xs">
                      <Row label="Product" value={toTitleCase(inst.PRODUCT_SHORT_NAME)} highlight />
                      <Row label="Category" value={toTitleCase(inst.PRODUCT_CATEGORY)} />
                      <Row label="Sub-category" value={toTitleCase(inst.PRODUCT_SUB_CATEGORY)} />
                      <Row label="Serial Number" value={inst.SERIAL_NUMBER} />
                      <Row label="Item Code" value={inst.ITEM_CODE} />
                      <Row label="Customer (Party)" value={toTitleCase(inst.PARTY_NAME)} />
                      <Row label="Party Number" value={inst.PARTY_NUMBER} />
                      <Row label="Contract Number" value={inst.CONTRACT_NUMBER} />
                      <Row label="Contract Type" value={inst.CONTRACT_TYPE} />
                      <Row label="Contract Start" value={fmtDate(inst.CONTRACT_START_DATE)} />
                      <Row label="Contract End" value={fmtDate(inst.CONTRACT_END_DATE)} />
                      <Row label="Contract Status" value={inst.CONTRACT_STATUS} status={inst.CONTRACT_STATUS} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {instrumentStatus === "not_found" && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{lookupMessage || "Instrument not found. You can still register below."}</span>
              </div>
            )}
            <h2 className="text-lg font-bold text-gray-900">Your Details</h2>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Full Name *</label>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Enter full name"
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="fullname-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Email ID *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter email"
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="email-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Mobile Number *</label>
              <div className="mt-1 flex items-center border border-gray-200 rounded-xl overflow-hidden">
                <span className="px-3 py-3 bg-gray-50 text-sm text-gray-500 border-r">+91</span>
                <input type="tel" value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number"
                  className="flex-1 px-3 py-3 text-sm focus:outline-none" data-testid="mobile-input" />
              </div>
            </div>
            <div className="relative">
              <label className="text-xs font-semibold text-gray-500 uppercase">State *</label>
              <input type="text" value={state || stateSearch} onChange={e => { setStateSearch(e.target.value); setState(""); setCity(""); setCitySearch(""); setShowStateDropdown(true); }}
                onFocus={() => setShowStateDropdown(true)} placeholder="Search state..."
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="state-input" />
              {showStateDropdown && filteredStates.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto">
                  {filteredStates.map(s => (
                    <button key={s} type="button" onMouseDown={e => e.preventDefault()} onClick={() => { setState(s); setStateSearch(""); setShowStateDropdown(false); setCity(""); setCitySearch(""); }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-[#E0F4F5] transition-colors" data-testid={`state-option-${s}`}>{s}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <label className="text-xs font-semibold text-gray-500 uppercase">City *</label>
              <input type="text" value={city || citySearch}
                onChange={e => { setCitySearch(e.target.value); setCity(""); setShowCityDropdown(true); }}
                onFocus={() => { if (state) setShowCityDropdown(true); }}
                onClick={() => { if (state) setShowCityDropdown(true); }}
                disabled={!state}
                placeholder={state ? "Search city..." : "Select state first"}
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99] disabled:bg-gray-50 disabled:text-gray-400"
                data-testid="city-input" />
              {showCityDropdown && state && filteredCities.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto" data-testid="city-dropdown-list">
                  {filteredCities.map(c => (
                    <button key={c} type="button" onMouseDown={e => e.preventDefault()} onClick={() => { setCity(c); setCitySearch(""); setShowCityDropdown(false); }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-[#E0F4F5] transition-colors" data-testid={`city-option-${c}`}>{c}</button>
                  ))}
                </div>
              )}
              {showCityDropdown && state && filteredCities.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg p-3 text-xs text-gray-500">No matching city. You can continue typing to search.</div>
              )}
            </div>

            {/* Referral code (optional) — entered code earns the referrer 100 pts on approval */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Referral Code <span className="text-gray-300 font-normal normal-case">(optional)</span></label>
              <div className="mt-1 relative">
                <input
                  type="text"
                  value={referralCode}
                  onChange={e => {
                    const v = e.target.value.toUpperCase().replace(/\s+/g, "");
                    setReferralCode(v);
                    if (referralStatus.valid !== null) setReferralStatus({ checking: false, valid: null, name: "" });
                  }}
                  onBlur={() => referralCode && validateReferral(referralCode)}
                  placeholder="e.g. MYERBA204FC6"
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none uppercase tracking-wider ${
                    referralStatus.valid === true ? "border-green-500 focus:border-green-600" :
                    referralStatus.valid === false ? "border-red-400 focus:border-red-500" :
                    "border-gray-200 focus:border-[#008A99]"
                  }`}
                  data-testid="referral-code-input"
                  maxLength={20}
                />
                {referralStatus.checking && <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                {!referralStatus.checking && referralStatus.valid === true && <CheckCircle className="w-4 h-4 text-green-500 absolute right-3 top-1/2 -translate-y-1/2" />}
                {!referralStatus.checking && referralStatus.valid === false && <AlertCircle className="w-4 h-4 text-red-500 absolute right-3 top-1/2 -translate-y-1/2" />}
              </div>
              {referralStatus.valid === true && (
                <p className="text-[11px] text-green-600 mt-1" data-testid="referral-code-valid-msg">Referred by {referralStatus.name} — they'll earn 100 pts once your KYC is approved.</p>
              )}
              {referralStatus.valid === false && (
                <p className="text-[11px] text-red-500 mt-1" data-testid="referral-code-invalid-msg">Invalid referral code. You can leave this blank.</p>
              )}
            </div>

            <button onClick={continueToLabProfile} disabled={!fullName || !email || !mobile || !state || !city}
              className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="details-continue-btn">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 3: Laboratory Profile (Point 22) */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-lab-profile">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Laboratory Profile</h2>
              <p className="text-sm text-gray-500 mt-1">Tell us about your lab and the instruments you use. All fields are required.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Lab Name *</label>
              <input type="text" value={labName} onChange={e => setLabName(e.target.value)} placeholder="Enter lab name"
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="lab-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Lab Telephone Number *</label>
              <input type="tel" value={labTel} onChange={e => setLabTel(e.target.value)} placeholder="Enter lab phone number"
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="lab-tel-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Lab Address *</label>
              <textarea value={labAddress} onChange={e => setLabAddress(e.target.value)} placeholder="Enter full lab address" rows={2}
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="lab-address-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Are you using an Erba model? *</label>
              <div className="flex gap-2 mt-1.5">
                <button type="button" onClick={() => chooseErba(true)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${usesErba === true ? "bg-[#008A99] text-white border-[#008A99]" : "bg-white text-gray-600 border-gray-200"}`} data-testid="erba-yes-btn">Yes</button>
                <button type="button" onClick={() => chooseErba(false)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${usesErba === false ? "bg-[#008A99] text-white border-[#008A99]" : "bg-white text-gray-600 border-gray-200"}`} data-testid="erba-no-btn">No</button>
              </div>
            </div>

            {usesErba !== null && (
              <div className="space-y-3" data-testid="lab-rows">
                <p className="text-xs font-semibold text-gray-500 uppercase">Instruments & Reagents *</p>
                {labRows.map((row, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-3 space-y-2 bg-gray-50/50" data-testid={`lab-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <button type="button" onClick={() => updateRow(idx, { is_erba: true, instrument: "", instrument_name: "", reagent: "", reagent_name: "" })}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold ${row.is_erba ? "bg-[#008A99] text-white" : "bg-white border border-gray-200 text-gray-500"}`} data-testid={`lab-row-erba-${idx}`}>Erba</button>
                        <button type="button" onClick={() => updateRow(idx, { is_erba: false, instrument: "", instrument_name: "", reagent: "", reagent_name: "" })}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold ${!row.is_erba ? "bg-[#008A99] text-white" : "bg-white border border-gray-200 text-gray-500"}`} data-testid={`lab-row-nonerba-${idx}`}>Non-Erba</button>
                      </div>
                      {labRows.length > 1 && (
                        <button type="button" onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600 p-1" data-testid={`lab-row-remove-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {row.is_erba ? (
                      <>
                        <select value={row.instrument}
                          onChange={e => { const code = e.target.value; const inst = allInstruments.find(i => i.instrument_code === code); updateRow(idx, { instrument: code, instrument_name: inst?.name || code, reagent: "", reagent_name: "" }); }}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99]" data-testid={`lab-row-instrument-select-${idx}`}>
                          <option value="">Select instrument</option>
                          {allInstruments.map(i => <option key={i.instrument_code} value={i.instrument_code}>{i.name || i.instrument_code}{i.segment ? ` — ${i.segment}` : ""}</option>)}
                        </select>
                        <select value={row.reagent} disabled={!row.instrument}
                          onChange={e => { const code = e.target.value; const p = allProducts.find(x => x.product_code === code); updateRow(idx, { reagent: code, reagent_name: p?.name || code }); }}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99] disabled:bg-gray-100 disabled:text-gray-400" data-testid={`lab-row-reagent-select-${idx}`}>
                          <option value="">{row.instrument ? "Select reagent" : "Select instrument first"}</option>
                          {reagentsFor(row.instrument).map(p => <option key={p.product_code} value={p.product_code}>{p.name}</option>)}
                        </select>
                      </>
                    ) : (
                      <>
                        <input type="text" value={row.instrument} onChange={e => updateRow(idx, { instrument: e.target.value, instrument_name: e.target.value })} placeholder="Instrument name"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99]" data-testid={`lab-row-instrument-text-${idx}`} />
                        <input type="text" value={row.reagent} onChange={e => updateRow(idx, { reagent: e.target.value, reagent_name: e.target.value })} placeholder="Reagent name"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99]" data-testid={`lab-row-reagent-text-${idx}`} />
                      </>
                    )}
                    <input type="number" min="0" value={row.avg_tests} onChange={e => updateRow(idx, { avg_tests: e.target.value })} placeholder="Average number of tests per month"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99]" data-testid={`lab-row-avgtests-${idx}`} />
                  </div>
                ))}
                <div className="flex gap-2">
                  <button type="button" onClick={() => addRow(usesErba)} className="flex-1 py-2.5 border border-dashed border-[#008A99] text-[#008A99] rounded-xl text-xs font-semibold flex items-center justify-center gap-1" data-testid="add-lab-row-btn">
                    <Plus className="w-3.5 h-3.5" /> Add instrument
                  </button>
                  <button type="button" onClick={() => addRow(!usesErba)} className="flex-1 py-2.5 border border-dashed border-gray-300 text-gray-500 rounded-xl text-xs font-semibold flex items-center justify-center gap-1" data-testid="add-other-lab-row-btn">
                    <Plus className="w-3.5 h-3.5" /> Add {usesErba ? "Non-Erba" : "Erba"} product
                  </button>
                </div>
              </div>
            )}

            <button onClick={sendOtp} disabled={loading}
              className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="send-otp-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to OTP <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}

        {/* Step 4: OTP Verification */}
        {step === 4 && (
          <div className="space-y-5" data-testid="step-otp">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Verify Mobile</h2>
              <p className="text-sm text-gray-500 mt-1">OTP sent to +91 {mobile}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Enter 6-digit OTP</label>
              <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter OTP"
                className="mt-1.5 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-center tracking-[0.5em] font-mono text-lg focus:outline-none focus:border-[#008A99]" data-testid="otp-input" />
            </div>
            <button onClick={verifyAndRegister} disabled={loading || otp.length !== 6}
              className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="verify-register-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verify & Register <CheckCircle className="w-4 h-4" /></>}
            </button>
            <button onClick={() => { setStep(2); setOtpSent(false); }} className="w-full text-center text-sm text-[#008A99] font-medium">Change mobile number</button>
          </div>
        )}

        {/* Step 5: KYC */}
        {step === 5 && (
          <div className="space-y-4" data-testid="step-kyc">
            <div>
              <h2 className="text-xl font-bold text-gray-900">KYC & Compliance</h2>
              <p className="text-sm text-gray-500 mt-1">Upload required documents</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">PAN Number <span className="text-gray-300 font-normal normal-case">(optional)</span></label>
              <input type="text" value={panNumber} onChange={e => setPanNumber(e.target.value.toUpperCase().slice(0, 10))} placeholder="ABCDE1234F"
                className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="pan-number-input" />
            </div>
            {[
              { label: "PAN Photograph (Optional)", field: "pan_photo", file: panFile, setFile: setPanFile },
              { label: "GST Registration Certificate (Optional)", field: "gst_cert", file: gstFile, setFile: setGstFile },
              { label: "Lab Certificate / Registration *", field: "lab_cert", file: labFile, setFile: setLabFile },
            ].map(({ label, field, file, setFile }) => (
              <div key={field}>
                <label className="text-xs font-semibold text-gray-500 uppercase">{label}</label>
                <label className="mt-1 flex items-center gap-3 px-4 py-3 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#008A99] hover:bg-[#E0F4F5]/30 transition-colors">
                  {uploading[field] ? <Loader2 className="w-5 h-5 animate-spin text-[#008A99]" /> : <Upload className="w-5 h-5 text-gray-400" />}
                  <span className="text-sm text-gray-500">{file ? file.name : "Choose PDF / JPG / PNG..."}</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={e => { const f = e.target.files[0]; if (f) { setFile(f); uploadFile(f, field, setFile); } e.target.value = ""; }} data-testid={`upload-${field}`} />
                </label>
              </div>
            ))}
            <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
              <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="mt-0.5 accent-[#008A99]" data-testid="terms-checkbox" />
              <span className="text-xs text-gray-600">I accept the Terms & Conditions and authorize TransAsia to verify my documents for the MyErba Loyalty Program.</span>
            </label>
            {/* Point 26 & 27 — mandatory location capture at final submission */}
            <div className={`rounded-xl p-3 border ${geoStatus === "granted" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="location-capture-block">
              <div className="flex items-start gap-2.5">
                <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${geoStatus === "granted" ? "text-emerald-600" : "text-amber-600"}`} />
                <div className="flex-1">
                  {geoStatus === "granted" ? (
                    <>
                      <p className="text-sm font-semibold text-emerald-700" data-testid="location-granted">Location captured</p>
                      <p className="text-[11px] text-emerald-600">Lat {Number(geoLat).toFixed(5)}, Long {Number(geoLng).toFixed(5)}</p>
                    </>
                  ) : geoStatus === "requesting" ? (
                    <p className="text-sm font-medium text-amber-700 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Getting your location…</p>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-amber-700">Location required to submit</p>
                      <p className="text-[11px] text-amber-700 mt-0.5" data-testid="location-guidance">{geoMsg || "Please turn ON your device location to submit your registration."}</p>
                      <button type="button" onClick={requestLocation} className="mt-2 text-xs font-bold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600" data-testid="location-retry-btn">Enable location / Retry</button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button onClick={submitKyc} disabled={loading || !labFile || !termsAccepted || geoStatus !== "granted"}
              className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-kyc-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit KYC <CheckCircle className="w-4 h-4" /></>}
            </button>
          </div>
        )}

        {/* Step 6: Under Review */}
        {step === 6 && (
          <div className="flex flex-col items-center text-center py-12 px-4" data-testid="step-under-review">
            <div className="w-20 h-20 rounded-full bg-[#E0F4F5] flex items-center justify-center mb-5">
              <CheckCircle className="w-10 h-10 text-[#008A99]" />
            </div>
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Registration Submitted</h2>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed max-w-xs">
              Thank you! Your details and KYC documents have been sent to the Transasia admin for verification.
            </p>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed max-w-xs">
              You'll be able to log in with your mobile number and OTP once your KYC is <span className="font-semibold text-[#008A99]">approved</span>.
            </p>
            <div className="mt-3 px-3 py-2 bg-[#E0F4F5] rounded-lg max-w-xs">
              <p className="text-xs text-[#008A99] font-semibold">The process may take 24–48 hours.</p>
            </div>
            <button onClick={() => navigate("/login")}
              className="mt-8 w-full max-w-xs py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
              data-testid="go-to-login-btn">
              Back to Login <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
